import type { AnimationItem } from 'lottie-web';

const maxBytes = 512 * 1024;
export interface ClaimAnimation {
  id: string;
  url: string;
}

function safeUrl(ref: ClaimAnimation): URL {
  if (!ref || typeof ref.id !== 'string' || !/^[a-f0-9]{64}$/.test(ref.id)) throw Error('invalid_asset');
  const url = new URL(ref.url);
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    !/^\/coupon-claim-lottie\/[a-f0-9]{64}\/[a-f0-9]{64}\.json$/.test(url.pathname) ||
    !url.pathname.endsWith('/' + ref.id + '.json')
  )
    throw Error('invalid_asset');
  return url;
}

export async function loadClaimAsset(ref: ClaimAnimation, signal: AbortSignal): Promise<any> {
  const response = await fetch(safeUrl(ref).href, {
    signal,
    credentials: 'omit',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
  });
  if (!response.ok || !response.body || Number(response.headers.get('content-length')) > maxBytes)
    throw Error('asset_unavailable');
  const reader = response.body.getReader(),
    chunks: Uint8Array[] = [];
  let size = 0;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > maxBytes) {
        await reader.cancel();
        throw Error('invalid_asset');
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const part of chunks) {
    bytes.set(part, offset);
    offset += part.length;
  }
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), (n) =>
    n.toString(16).padStart(2, '0')
  ).join('');
  if (hash !== ref.id) throw Error('invalid_asset');
  const data = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  if (data.segments !== undefined && (!Array.isArray(data.segments) || data.segments.length))
    throw Error('invalid_asset');
  const seconds = (data.op - data.ip) / data.fr;
  if (!(seconds > 0 && seconds <= 15 && data.w > 0 && data.h > 0 && Array.isArray(data.layers)))
    throw Error('invalid_asset');
  return data;
}

// A Lottie "complete" event also fires for wholly transparent animations. Only
// read frames until the first visible pixel, using a bounded canvas at display scale.
export function observeClaimPlayback(
  player: AnimationItem,
  container: HTMLElement,
  firstPaint: () => void,
  ended: (painted: boolean) => void
): void {
  const sample = document.createElement('canvas');
  const context = sample.getContext('2d', { willReadFrequently: true });
  let painted = false;
  let settled = false;
  const finish = (ok: boolean) => {
    if (settled) return;
    settled = true;
    ended(ok);
  };
  const inspect = () => {
    if (settled || painted) return;
    try {
      const canvas = container.querySelector('canvas');
      if (!canvas?.width || !canvas.height) return;
      if (!context) return finish(false);
      const scale = Math.min(1, 256 / Math.max(canvas.width, canvas.height));
      const width = Math.max(1, Math.round(canvas.width * scale));
      const height = Math.max(1, Math.round(canvas.height * scale));
      if (sample.width !== width || sample.height !== height) {
        sample.width = width;
        sample.height = height;
        context.imageSmoothingQuality = 'high';
      }
      context.clearRect(0, 0, width, height);
      context.drawImage(canvas, 0, 0, width, height);
      const pixels = context.getImageData(0, 0, width, height).data;
      for (let alpha = 3; alpha < pixels.length; alpha += 4) {
        if (pixels[alpha] === 0) continue;
        painted = true;
        firstPaint();
        return;
      }
    } catch {
      finish(false);
    }
  };
  player.addEventListener('drawnFrame', inspect);
  player.addEventListener('complete', () => {
    inspect();
    finish(painted);
  });
  player.addEventListener('data_failed', () => finish(false));
  player.addEventListener('error', () => finish(false));
  inspect();
}
